import React from 'react'
import {Box, Flex, Grid} from '@chakra-ui/react'
import {remCalc} from '../../../theme/_utils'
import MenuGroupedCategories from '../../list-menu/partials/MenuGroupedCategories'
import MenuVerticalCard from '../../list-menu/partials/MenuVerticalCard'
import MenuSimpleCard from '../../list-menu/partials/MenuSimpleCard'
import {getTemplateLinks} from '../../list-menu/partials/templates/shared'
import {
    DrawerLinkButtons,
    compactGap,
    drawerBottomPadding,
    drawerTemplatePropTypes,
    fullHeightChildSx,
    groupedCategoriesProps,
    templateGridGap,
    templateGridGapSx,
} from './shared'

const gridColumns = {base: '1fr', sm: '1fr', md: '1fr 2fr', lg: '1fr 2fr'}
const desktopDisplay = {base: 'none', sm: 'none', md: 'flex', lg: 'flex'}
const mobileDisplay = {base: 'flex', sm: 'flex', md: 'none', lg: 'none'}
const cardMinHeight = remCalc(120)
const mobileCardSizing = {
    minW: {base: remCalc(145), sm: remCalc(145)},
    width: '100%',
    aspectRatio: 1.5,
}

export const getDrawerTemplateOneData = (primarycategory) => {
    const verticalCards = Array.isArray(primarycategory?.images) ? primarycategory.images : []
    const simpleCards = primarycategory?.cards ?? []

    return {
        categoryGroup: primarycategory?.category_group ?? [],
        links: getTemplateLinks(primarycategory),
        verticalCards,
        simpleCards,
        mobileGridCards: [
            ...verticalCards.slice(0, 2).map((card) => ({type: 'vertical', card})),
            ...simpleCards.slice(0, 2).map((card) => ({type: 'simple', card})),
        ],
        mobileOverflowSimpleCards: simpleCards.slice(2),
    }
}

const DrawerTemplateOne = ({primarycategory, categoryData, locale, styles}) => {
    const {
        categoryGroup,
        links,
        verticalCards,
        simpleCards,
        mobileGridCards,
        mobileOverflowSimpleCards,
    } = getDrawerTemplateOneData(primarycategory)

    return (
        <Box w="100%" pb={drawerBottomPadding} data-primarycategory-id={primarycategory?.uid}>
            <Grid templateColumns={gridColumns} gap={templateGridGap} sx={templateGridGapSx}>
                <Flex
                    flexDir="column"
                    gap={templateGridGap}
                    sx={templateGridGapSx}
                    gridColumn={{base: '1 / -1', sm: '1 / -1', md: '1 / 2'}}
                >
                    {categoryData && categoryGroup.length > 0 && (
                        <Box {...groupedCategoriesProps}>
                            <MenuGroupedCategories
                                groupedCategories={categoryGroup}
                                locale={locale}
                                categoryData={categoryData}
                                styles={styles}
                                groupLinkGap={remCalc(6)}
                            />
                        </Box>
                    )}

                    {links.length > 0 && (
                        <Flex display={desktopDisplay} flexDir="column" gap={remCalc(6)}>
                            <DrawerLinkButtons
                                links={links}
                                categoryData={categoryData}
                                locale={locale}
                                styles={styles}
                            />
                        </Flex>
                    )}
                </Flex>

                <Flex display={desktopDisplay} flexDir="column" gap={compactGap} gridColumn="2 / 3">
                    {verticalCards.length > 0 && (
                        <Grid
                            templateColumns="repeat(2, minmax(0, 1fr))"
                            gap={compactGap}
                            minH={{md: remCalc(406)}}
                            minW={{md: remCalc(580)}}
                        >
                            {verticalCards.slice(0, 2).map((card, idx) => (
                                <Box
                                    key={card?._metadata?.uid ?? `drawer-menu-vertical-card-${idx}`}
                                    minH={cardMinHeight}
                                    sx={fullHeightChildSx}
                                >
                                    <MenuVerticalCard
                                        card={card}
                                        categoriesData={categoryData}
                                        locale={locale}
                                    />
                                </Box>
                            ))}
                        </Grid>
                    )}

                    {simpleCards.length > 0 && (
                        <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={compactGap}>
                            {simpleCards.map((card, idx) => (
                                <Box
                                    key={`drawer-temp1-menu-simple-card-md-${idx}`}
                                    minH={cardMinHeight}
                                    h={cardMinHeight}
                                    sx={fullHeightChildSx}
                                >
                                    <MenuSimpleCard
                                        card={card}
                                        categoriesData={categoryData}
                                        locale={locale}
                                    />
                                </Box>
                            ))}
                        </Grid>
                    )}
                </Flex>

                {(mobileGridCards.length > 0 ||
                    mobileOverflowSimpleCards.length > 0 ||
                    links.length > 0) && (
                    <Flex
                        display={mobileDisplay}
                        gridColumn="1 / -1"
                        flexDir="column"
                        gap={{
                            base: remCalc(24),
                            sm: remCalc(24),
                            md: remCalc(32),
                        }}
                    >
                        {(mobileGridCards.length > 0 || mobileOverflowSimpleCards.length > 0) && (
                            <Flex flexDir="column" gap={compactGap}>
                                {mobileGridCards.length > 0 && (
                                    <Grid
                                        templateColumns="repeat(2, minmax(0, 1fr))"
                                        gap={compactGap}
                                    >
                                        {mobileGridCards.map((item, idx) =>
                                            item.type === 'vertical' ? (
                                                <Box
                                                    key={
                                                        item.card?._metadata?.uid ??
                                                        `drawer-temp1-mobile-vertical-card-${idx}`
                                                    }
                                                    minH={cardMinHeight}
                                                    {...mobileCardSizing}
                                                    sx={fullHeightChildSx}
                                                >
                                                    <MenuVerticalCard
                                                        card={item.card}
                                                        categoriesData={categoryData}
                                                        locale={locale}
                                                        showChevron={false}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box
                                                    key={`drawer-temp1-mobile-simple-card-grid-${idx}`}
                                                    minH={cardMinHeight}
                                                    h={cardMinHeight}
                                                    {...mobileCardSizing}
                                                    sx={fullHeightChildSx}
                                                >
                                                    <MenuSimpleCard
                                                        card={item.card}
                                                        categoriesData={categoryData}
                                                        locale={locale}
                                                        showSubtitle={false}
                                                        showChevron={false}
                                                    />
                                                </Box>
                                            ),
                                        )}
                                    </Grid>
                                )}

                                {mobileOverflowSimpleCards.map((card, idx) => (
                                    <Box
                                        key={`drawer-temp1-mobile-simple-card-full-${idx}`}
                                        w="100%"
                                        minH={cardMinHeight}
                                        h={cardMinHeight}
                                        {...mobileCardSizing}
                                        sx={fullHeightChildSx}
                                    >
                                        <MenuSimpleCard
                                            card={card}
                                            categoriesData={categoryData}
                                            locale={locale}
                                            showSubtitle={false}
                                            showChevron={false}
                                        />
                                    </Box>
                                ))}
                            </Flex>
                        )}

                        {links.length > 0 && (
                            <Flex flexDir="column" gap={remCalc(6)}>
                                <DrawerLinkButtons
                                    links={links}
                                    categoryData={categoryData}
                                    locale={locale}
                                    styles={styles}
                                />
                            </Flex>
                        )}
                    </Flex>
                )}
            </Grid>
        </Box>
    )
}

DrawerTemplateOne.propTypes = drawerTemplatePropTypes

export default DrawerTemplateOne
